/** @format */

import { type FC, useMemo, useState, useEffect } from "react";
import { useFeedbackStore } from "../../store/useFeedbackStore";
import type { Step } from "../../types/sop";
import type { RegenerationDecision } from "../../store/useReviewCommitStore";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { Toast } from "../../components/ui/Toast";
import { useToast } from "../../hooks/useToast";
import StepEditor from "./StepEditor";
import type { FilteredMedia } from "../../utils/mediaFiltering";
import "./StepEditor.css";

export interface StepCandidateReviewProps {
  originalStep: Step;
  candidate: Step | null;
  stepIndex: number;
  /** When false (e.g. super admin viewing someone else's version), Accept/Request Change block is hidden. */
  showApproveDenyActions: boolean;
  canEdit: boolean;
  /** Whether the current user can edit the candidate contents (not just accept/request change). */
  canEditCandidate: boolean;
  /** When true, hide candidate preview/editor (e.g. super-admin after sending to owner). */
  waitingForOwnerReview?: boolean;
  onCandidateChange: (updated: Step) => void;
  onAccept: () => void;
  onRequestChange: () => void;
  /** When starting the "Request change" flow while the step was Accepted — parent should clear accept (e.g. undecide). */
  onBeforeEnterRequestChangePending?: () => void;
  decision: RegenerationDecision | undefined;
  sopId: string;
  sopTitle: string;
  /** The version this candidate belongs to — passed to StepEditor so the feedback bubble menu has a versionId. */
  versionId?: string;
  /** The candidate record ID — when provided, feedback submitted via the bubble menu targets this candidate. */
  candidateId?: string;
  onFileUpload: (
    file: File,
    isLocal?: boolean,
  ) => Promise<{ imageRefId: string; downloadUrl: string }>;
  getCurrentUrl: (imageRefId: string) => Promise<string | null>;
  getImageUrls: (
    refIds: string[],
  ) => Promise<Record<string, { url: string; expiresAt: number }>>;
  setImageUrls: (
    updater: (
      prev: Record<string, { url: string; expiresAt: number }>,
    ) => Record<string, { url: string; expiresAt: number }>,
  ) => void;
  imageUrls: Record<string, { url: string; expiresAt: number }>;
  media: FilteredMedia | null;
  mediaLoading: boolean;
  mediaError: string | null;
  stepsLength: number;
}

export const StepCandidateReview: FC<StepCandidateReviewProps> = ({
  originalStep: _originalStep,
  candidate,
  stepIndex,
  showApproveDenyActions,
  canEdit,
  canEditCandidate,
  waitingForOwnerReview = false,
  onCandidateChange,
  onAccept,
  onRequestChange,
  onBeforeEnterRequestChangePending,
  decision,
  sopId,
  sopTitle,
  versionId,
  candidateId,
  onFileUpload,
  getCurrentUrl,
  getImageUrls,
  setImageUrls,
  imageUrls,
  media,
  mediaLoading,
  mediaError,
  stepsLength,
}) => {
  // pendingRequest = user clicked "Request Change" but hasn't confirmed yet (waiting for feedback)
  const [pendingRequest, setPendingRequest] = useState(false);
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  const { toast, showToast, hideToast } = useToast();

  // Reset pending state when navigating to a different candidate
  useEffect(() => {
    setPendingRequest(false);
    setShowAcceptConfirm(false);
    hideToast();
  }, [stepIndex, candidateId, hideToast]);

  // Watch the feedback store for any submitted item targeting this candidate
  const feedbackByVersion = useFeedbackStore((s) => s.feedbackByVersion);
  // Only unresolved feedback counts — resolved items have already been addressed
  // and must not be used to gate or auto-trigger a new change request.
  const hasCandidateFeedback = useMemo(() => {
    if (!candidateId || !sopId || !versionId) return false;
    const key = `${sopId}:${versionId}`;
    return (feedbackByVersion[key] ?? []).some(
      (f) => f.candidateId === candidateId && f.status !== "resolved",
    );
  }, [feedbackByVersion, candidateId, sopId, versionId]);

  // Auto-commit: as soon as the first feedback is submitted while in pending
  // state, mark the step as a change request — no manual "Confirm" needed.
  useEffect(() => {
    if (pendingRequest && hasCandidateFeedback) {
      setPendingRequest(false);
      hideToast();
      onRequestChange();
    }
    // onRequestChange identity is stable (comes from parent handler); intentionally
    // not listed to avoid double-firing if parent re-renders between the two state updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCandidateFeedback, pendingRequest]);

  if (!candidate) {
    return (
      <div className="step-candidate-review step-candidate-review--no-candidate">
        <p className="step-candidate-review-no-candidate-msg">
          No AI version for this step. Regenerate with AI and select this step
          to get an AI version.
        </p>
      </div>
    );
  }

  if (waitingForOwnerReview) {
    return (
      <div className="step-candidate-review">
        <div className="mb-4">
          <span className="text-[11px] px-2 py-1 rounded bg-amber-500/15 border border-amber-500/25 text-amber-200/90">
            Waiting for candidate review
          </span>
        </div>
        <div className="text-sm text-slate-200/90">
          None of the SOP editors have submitted the decision yet. Candidate
          content is locked until a decision is made.
        </div>
      </div>
    );
  }

  return (
    <div className="step-candidate-review mt-3 max-w-[1000px] mx-auto">
      <div className="step-candidate-review-comparison">
        <div className="step-candidate-review-column step-candidate-review-column--editor">
          <h4 className="step-candidate-review-column-title">
            AI VERSION
            {showApproveDenyActions && canEdit && (
              <div className="step-candidate-review-owner-block">
                <div
                  className={`flex align-center justify-end step-candidate-review-actions ${
                    decision === "accept"
                      ? "step-candidate-review-actions--accept"
                      : decision === "request_change" || pendingRequest
                        ? "step-candidate-review-actions--request_change"
                        : ""
                  }`}
                >
                  <button
                    type="button"
                    className={`step-candidate-review-accept-btn ${decision === "accept" ? "step-candidate-review-btn--active" : ""}`}
                    onClick={() => {
                      setShowAcceptConfirm(true);
                    }}
                    aria-pressed={decision === "accept"}
                  >
                    {decision === "accept" ? "Accepted" : "Accept"}
                  </button>

                  <button
                    type="button"
                    className={`step-candidate-review-request-btn ${
                      decision === "request_change" || pendingRequest
                        ? "step-candidate-review-btn--active"
                        : ""
                    }`}
                    onClick={() => {
                      if (decision === "request_change") {
                        // Toggle off — undo the change request
                        onRequestChange();
                      } else if (pendingRequest) {
                        // Cancel pending mode
                        setPendingRequest(false);
                        hideToast();
                      } else {
                        if (decision === "accept") {
                          onBeforeEnterRequestChangePending?.();
                        }
                        setPendingRequest(true);
                        showToast(
                          "Add feedback to request a change",
                          "Select text in the AI version and click Suggest edit to explain what needs to change.",
                          "warning",
                          12_000,
                        );
                      }
                    }}
                    aria-pressed={
                      decision === "request_change" || pendingRequest
                    }
                  >
                    {decision === "request_change"
                      ? "Change Requested"
                      : "Request Change"}
                  </button>
                </div>
              </div>
            )}
          </h4>
          <div className="step-candidate-review-editor-wrap">
            <StepEditor
              index={stepIndex}
              step={candidate}
              sopId={sopId}
              versionId={versionId}
              candidateId={candidateId}
              feedbackEnabled={pendingRequest || decision === "request_change"}
              onFileUpload={onFileUpload}
              onStepChange={(updated) => onCandidateChange(updated)}
              readOnly={!canEditCandidate}
              canDelete={false}
              canEdit={canEditCandidate}
              sopTitle={sopTitle}
              stepsLength={stepsLength}
              getCurrentUrl={getCurrentUrl}
              getImageUrls={getImageUrls}
              setImageUrls={setImageUrls}
              imageUrls={imageUrls}
              media={media}
              mediaLoading={mediaLoading}
              mediaError={mediaError}
            />
          </div>
        </div>
      </div>

      <Toast
        open={toast.open}
        onOpenChange={(open) => {
          if (!open) hideToast();
        }}
        title={toast.title ?? ""}
        description={toast.description}
        type={toast.type}
        duration={toast.duration}
      />

      <ConfirmDialog
        open={showAcceptConfirm}
        onClose={() => setShowAcceptConfirm(false)}
        onConfirm={() => {
          setShowAcceptConfirm(false);
          setPendingRequest(false);
          hideToast();
          onAccept();
        }}
        title={
          decision === "accept"
            ? "Remove acceptance?"
            : "Accept this AI version?"
        }
        description={
          decision === "accept"
            ? "You can choose Accept or Request change again before you finish the review with Done."
            : hasCandidateFeedback
              ? "You still have feedback on this step. If you accept it and later click Done to finish the review, that feedback will be permanently discarded."
              : "This step will be marked as accepted. You can change your mind until you finish the review with Done."
        }
        confirmLabel={decision === "accept" ? "Remove" : "Accept"}
        cancelLabel="Cancel"
        variant={decision === "accept" ? "danger" : "default"}
      />
    </div>
  );
};
